import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  COMMUNICATION_OWNER_TYPES,
  ownerTypeLabel,
  type CommunicationChannel,
  type CommunicationCounterparty,
  type CommunicationDirection,
  type CreateCommunicationLogDto,
  type CreateParticipantDto,
  type PlacementRefDto,
} from "@/api/communications";
// The per-channel rules. ONE definition, shared with the buyer "Log
// contact" modal — the two forms ask the same questions of the same five
// channels, and a second copy is how they drifted far enough that three of
// those channels could not be logged from the quick modal at all.
import {
  CALL_OUTCOMES,
  CHANNEL_LABELS,
  COMMUNICATION_CHANNELS,
  COMMUNICATION_DIRECTIONS,
  DEFAULT_MEETING_MODALITY,
  MEETING_MODALITIES,
  fullFormShows,
  isRequired,
  validateChannelFields,
} from "@/lib/communicationChannelRules";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
// The wall-clock ⇄ UTC bridge. It lives under `pages/calendar/lib` because the
// appointment form needed it first; it is pure, takes an explicit zone, and
// carries the DST-gap caveat in its own doc block. A second copy here would be
// a second set of edge cases to keep in step.
import {
  utcIsoToDateInput,
  utcIsoToTimeInput,
  wallClockToUtcIso,
} from "@/pages/calendar/lib/tz";
import { useCreateCommunication } from "../hooks/useCommunications";
import { ContactPickerInput } from "./ContactPickerInput";
import { PlacementOwnerPicker } from "./PlacementOwnerPicker";

/** The one owner type that produces a package, and so the only one for which
 *  eligibility and counterparty are real questions. */
const SUBMISSION_OWNER_TYPE = "buyer_submission";

const COUNTERPARTIES: { value: CommunicationCounterparty; label: string }[] = [
  { value: "Buyer", label: "Buyer" },
  { value: "ListingAgent", label: "Listing agent" },
  { value: "Seller", label: "Seller" },
  { value: "Other", label: "Other" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Entity-prefilled mode (Comm Log Standard §9.3): pre-populate the
   * placement list with the launching entity. The user can ADD other
   * placements but cannot remove the originating one.
   */
  preFilledOwner?: { type: string; id: string; label?: string };
  /** Opens on this channel instead of Call (Who's calling's LOG CALL passes
   *  Call explicitly, Addendum B). */
  preFilledChannel?: CommunicationChannel;
  /** Opens with this counterparty chosen (Who's calling's LOG CALL: Listing
   *  agent). The reader can still change it. */
  preFilledCounterparty?: CommunicationCounterparty;
  /**
   * When true (central inbox mode), navigate to the new entry's detail
   * page after save. When false (entity tab mode), close + toast and
   * stay on the current page so the entity tab refreshes in place.
   */
  navigateOnSave?: boolean;
  onCreated?: (entryId: string) => void;
}

interface ParticipantRow extends CreateParticipantDto {
  /** Stable client-only key — react list reconciliation. */
  _key: string;
  /**
   * Set on the row auto-seeded from the current user (FIND-08). Marks the
   * row as the convenience default; the user may still edit or remove it,
   * and once removed it is not re-added for this open session.
   */
  wasSelfPrefilled?: boolean;
}

interface PlacementRow extends PlacementRefDto {
  _key: string;
  /** Set when the row is the launching entity in §9.3 mode; locks delete only. */
  locked?: boolean;
  label?: string;
}

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Initial participants list (FIND-08). The current user almost always IS a
 * participant — that's how they know to log the call — so seed one row from
 * them, mirroring how Placements seeds the launching entity. Falls back to a
 * single blank row when the user isn't loaded yet.
 */
function initialParticipants(
  user: { displayName: string; email: string } | null | undefined,
): ParticipantRow[] {
  if (!user?.displayName) {
    return [{ _key: newKey(), displayName: "" }];
  }
  return [
    {
      _key: newKey(),
      displayName: user.displayName,
      phoneOrEmail: user.email || null,
      roleHint: "Self",
      wasSelfPrefilled: true,
    },
  ];
}

/**
 * Now, rounded to the nearest 5 minutes, as a `<input type="datetime-local">`
 * value in the USER'S CONFIGURED ZONE.
 *
 * 🔴 IT USED TO READ THE BROWSER'S. `getFullYear()`/`getHours()` and friends
 * are local-time getters, and `<input type="datetime-local">` has no zone of
 * its own, so the field showed the machine's wall clock while every other
 * timestamp on the screen showed `MeDto.timeZone`. Two separate consequences,
 * and only the second moves data:
 *
 *   • DISPLAY — a London user with Phoenix configured opened the modal at
 *     09:00 and read "09:00" here beside "02:00" everywhere else.
 *   • TYPED ENTRY — `new Date(value)` parses a datetime-local string in the
 *     BROWSER's zone, so typing "14:00" meaning 14:00 Phoenix recorded 14:00
 *     London, seven hours out. Both halves had to move together: seeding in
 *     the configured zone while still parsing in the browser's would take a
 *     correct default and record it wrong.
 *
 * An UNTOUCHED seed round-tripped losslessly before (browser-local out,
 * browser-local back), which is why nothing ever failed.
 */
/**
 * Split a `<input type="datetime-local">` value into the `YYYY-MM-DD` and
 * `HH:mm` halves `wallClockToUtcIso` expects. Returns null for anything that
 * is not that exact shape, so a malformed value is REFUSED at validation
 * rather than silently resolved to some other instant.
 */
function splitDatetimeLocal(value: string): [string, string] | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  return m ? [m[1], m[2]] : null;
}

function nowDatetimeLocalRounded(tz: string | null): string {
  const ms = 5 * 60 * 1000;
  const rounded = new Date(Math.round(Date.now() / ms) * ms);
  const iso = rounded.toISOString();
  return `${utcIsoToDateInput(iso, tz)}T${utcIsoToTimeInput(iso, tz)}`;
}

/**
 * <c>&lt;LogCommunicationModal&gt;</c> — the single shared modal for
 * logging an external communication. Used by every quick-log button
 * across the platform plus the inbox's <c>+ New</c>.
 *
 * Channel-conditional fields per Comm Log Standard §9.4:
 *  - Call/Voicemail → outcome, phone
 *  - Email → subject, from, to
 *  - SMS → phone
 *  - Meeting → subject, modality (no direction)
 */
export function LogCommunicationModal({
  open,
  onClose,
  preFilledOwner,
  preFilledChannel,
  preFilledCounterparty,
  navigateOnSave = false,
  onCreated,
}: Props) {
  const create = useCreateCommunication();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tz } = useDateFormatter();

  const [channel, setChannel] = useState<CommunicationChannel>("Call");
  const [direction, setDirection] = useState<CommunicationDirection>("Outbound");
  const [occurredAt, setOccurredAt] = useState<string>(() => nowDatetimeLocalRounded(tz));
  const [durationMinutes, setDurationMinutes] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [outcome, setOutcome] = useState<string>("");
  const [modality, setModality] =
    useState<(typeof MEETING_MODALITIES)[number]>(DEFAULT_MEETING_MODALITY);
  const [emailFrom, setEmailFrom] = useState<string>("");
  const [emailTo, setEmailTo] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [counterpartyRole, setCounterpartyRole] =
    useState<CommunicationCounterparty | "">("");
  const [participants, setParticipants] = useState<ParticipantRow[]>(() =>
    initialParticipants(user),
  );
  const [placements, setPlacements] = useState<PlacementRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form whenever the modal re-opens. Skipping this on close-only
  // means a Save → Cancel sequence won't carry stale state into the
  // next open, which Tasks v1.1 PR 2 flagged as a regression.
  useEffect(() => {
    if (!open) return;
    setChannel(preFilledChannel ?? "Call");
    setDirection("Outbound");
    setOccurredAt(nowDatetimeLocalRounded(tz));
    setDurationMinutes("");
    setSubject("");
    setBody("");
    setOutcome("");
    setModality("In-Person");
    setEmailFrom("");
    setEmailTo("");
    setPhone("");
    setParticipants(initialParticipants(user));
    setCounterpartyRole(preFilledCounterparty ?? "");
    setErrors({});
    setPlacements(
      preFilledOwner
        ? [
            {
              _key: newKey(),
              ownerType: preFilledOwner.type,
              ownerId: preFilledOwner.id,
              label: preFilledOwner.label,
              locked: true,
            },
          ]
        : [{ _key: newKey(), ownerType: "broker", ownerId: "" }],
    );
    // Depend on primitive fields, not object references — some callers
    // (e.g. CommunicationsTabPanel) construct a fresh `preFilledOwner`
    // object on every render, and `useAuth()` can hand us a fresh `user`
    // object too. Depending on the objects themselves would re-run this
    // reset on every incidental parent re-render and silently clobber
    // in-progress input.
  }, [
    open,
    preFilledOwner?.type,
    preFilledOwner?.id,
    preFilledOwner?.label,
    preFilledChannel,
    preFilledCounterparty,
    user?.id,
  ]);

  // 🔴 Per-ENTRY, while packageEligible is per-PLACEMENT. The counterparty of a
  // conversation is invariant across where it is filed — call the buyer about two
  // submissions and you called the buyer, twice over — so a per-placement
  // counterparty could hold two contradictory answers to one question about the
  // real world. Eligibility genuinely differs per placement. Asked together in
  // one block, stored at different levels.
  const hasSubmissionPlacement = placements.some(
    (p) => p.ownerType === SUBMISSION_OWNER_TYPE && p.ownerId.trim(),
  );

  // The full form is the complete record-keeping surface, so it shows every
  // field the channel does not hide — including the ones the channel merely
  // offers. The quick modal reads the same spec through a narrower rule.
  const showDirection = fullFormShows(channel, "direction");
  const showSubject = fullFormShows(channel, "subject");
  const showOutcome = fullFormShows(channel, "outcome");
  const showEmailFields = channel === "Email";
  const showPhone =
    channel === "Call" || channel === "Voicemail" || channel === "Sms";
  const showModality = fullFormShows(channel, "modality");

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!body.trim()) next.body = "Notes are required.";
    if (!occurredAt) next.occurredAt = "Occurred-at is required.";
    else if (splitDatetimeLocal(occurredAt) === null) {
      // Reachable only if the browser hands back something other than the
      // datetime-local shape. Refusing beats substituting: the alternative is
      // recording a moment nobody chose.
      next.occurredAt = "Enter a valid date and time.";
    }
    // Direction / Subject / Outcome / Modality — the same check the quick
    // modal runs, over the same spec, with this form's own rule for which
    // questions it asked. A form must never refuse a value it never
    // requested, which is why `shows` is a parameter and not an assumption.
    Object.assign(
      next,
      validateChannelFields(
        channel,
        { direction, subject, outcome, modality },
        fullFormShows,
      ),
    );
    if (participants.filter((p) => p.displayName.trim()).length === 0) {
      next.participants = "At least one participant is required.";
    }
    const validPlacements = placements.filter(
      (p) => p.ownerType && p.ownerId.trim(),
    );
    if (validPlacements.length === 0) {
      next.placements = "At least one placement is required.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    // The field holds a WALL CLOCK in the user's configured zone, so it is
    // resolved WITH that zone. `new Date(occurredAt)` would read it in the
    // browser's — the typed-entry half of this defect. `validate()` has
    // already refused a malformed value, so the fallback is unreachable and
    // exists only so the type is not nullable.
    const parts = splitDatetimeLocal(occurredAt);
    const occurredAtIso =
      (parts && wallClockToUtcIso(parts[0], parts[1], tz)) ?? new Date().toISOString();

    const payload: CreateCommunicationLogDto = {
      channel,
      direction: showDirection ? direction : null,
      // The field holds a WALL CLOCK in the user's configured zone, so it is
      // resolved with that zone rather than by `new Date(...)`, which would
      // read it in the browser's.
      occurredAt: occurredAtIso,
      durationMinutes: durationMinutes ? Number(durationMinutes) : null,
      subject: showSubject ? subject.trim() || null : null,
      body: body.trim(),
      outcome: showOutcome ? outcome || null : null,
      modality: showModality ? modality : null,
      emailFromAddress: showEmailFields ? emailFrom.trim() || null : null,
      emailToAddress: showEmailFields ? emailTo.trim() || null : null,
      externalPhoneNumber: showPhone ? phone.trim() || null : null,
      externalPartyName: null,
      placements: placements
        .filter((p) => p.ownerType && p.ownerId.trim())
        .map(({ ownerType, ownerId, packageEligible }) => ({
          ownerType,
          ownerId: ownerId.trim(),
          // Only ever true on a submission row; the server forces it false for
          // every other owner type regardless of what is sent.
          packageEligible: ownerType === SUBMISSION_OWNER_TYPE && !!packageEligible,
        })),
      counterpartyRole: hasSubmissionPlacement ? counterpartyRole || null : null,
      participants: participants
        .filter((p) => p.displayName.trim())
        .map(({ displayName, phoneOrEmail, roleHint, contactId }) => ({
          displayName: displayName.trim(),
          phoneOrEmail: phoneOrEmail?.trim() || null,
          roleHint: roleHint?.trim() || null,
          contactId: contactId ?? null,
        })),
    };

    try {
      const created = await create.mutateAsync(payload);
      toast({ message: "Communication logged.", tone: "success" });
      onCreated?.(created.id);
      onClose();
      if (navigateOnSave) navigate(`/communications/${created.id}`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.errors?.[0] ?? err.message
          : "Could not log communication.";
      toast({ message: msg, tone: "error" });
    }
  };

  const setParticipantField = (
    key: string,
    patch: Partial<CreateParticipantDto>,
  ) => {
    setParticipants((rows) =>
      rows.map((r) => (r._key === key ? { ...r, ...patch } : r)),
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Log communication"
      description="Record an external call, voicemail, email, SMS, or meeting."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={create.isPending} type="button">
            Save entry
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField label="Channel" required htmlFor="lc-channel">
            <Select
              id="lc-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as CommunicationChannel)}
            >
              {COMMUNICATION_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABELS[c]}
                </option>
              ))}
            </Select>
          </FormField>

          {showDirection && (
            <FormField
              label="Direction"
              required
              htmlFor="lc-direction"
              error={errors.direction}
            >
              <Select
                id="lc-direction"
                value={direction}
                onChange={(e) =>
                  setDirection(e.target.value as CommunicationDirection)
                }
              >
                {COMMUNICATION_DIRECTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField
            label="Occurred at"
            required
            htmlFor="lc-occurred"
            error={errors.occurredAt}
          >
            <Input
              id="lc-occurred"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              invalid={Boolean(errors.occurredAt)}
            />
          </FormField>
          <FormField label="Duration (minutes)" htmlFor="lc-duration">
            <Input
              id="lc-duration"
              type="number"
              min={0}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              placeholder="Optional"
            />
          </FormField>
        </div>

        {showSubject && (
          <FormField
            label="Subject"
            required={isRequired(channel, "subject")}
            htmlFor="lc-subject"
            error={errors.subject}
          >
            <Input
              id="lc-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              invalid={Boolean(errors.subject)}
            />
          </FormField>
        )}

        {showOutcome && (
          <FormField
            label="Outcome"
            required={isRequired(channel, "outcome")}
            htmlFor="lc-outcome"
            error={errors.outcome}
          >
            <Select
              id="lc-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            >
              <option value="">— Select outcome —</option>
              {CALL_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        {showEmailFields && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <FormField label="From address" htmlFor="lc-from">
              <Input
                id="lc-from"
                value={emailFrom}
                onChange={(e) => setEmailFrom(e.target.value)}
                placeholder="counterparty@example.com"
              />
            </FormField>
            <FormField label="To address" htmlFor="lc-to">
              <Input
                id="lc-to"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="you@phoenix.example"
              />
            </FormField>
          </div>
        )}

        {showPhone && (
          <FormField label="Phone number" htmlFor="lc-phone">
            <Input
              id="lc-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1-555-555-0123"
            />
          </FormField>
        )}

        {showModality && (
          <FormField
            label="Modality"
            required={isRequired(channel, "modality")}
            htmlFor="lc-modality"
            error={errors.modality}
          >
            <Select
              id="lc-modality"
              value={modality}
              onChange={(e) =>
                setModality(
                  e.target.value as (typeof MEETING_MODALITIES)[number],
                )
              }
            >
              {MEETING_MODALITIES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField
          label="Notes"
          required
          htmlFor="lc-body"
          error={errors.body}
          helperText="Markdown supported. What was discussed, decisions made, follow-ups."
        >
          <Textarea
            id="lc-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            autoFocus
            invalid={Boolean(errors.body)}
          />
        </FormField>

        <ParticipantsEditor
          rows={participants}
          error={errors.participants}
          onChange={(rows) => setParticipants(rows)}
          onPatch={setParticipantField}
        />

        <PlacementsEditor
          rows={placements}
          error={errors.placements}
          onChange={(rows) => setPlacements(rows)}
        />

        {/* Only when a submission is placed. Keyed on the PLACEMENT, not on
            where the modal was opened from: this modal is mounted by the central
            inbox with no pre-filled owner and by twenty tab panels, and a
            submission can be placed from any of them. */}
        {hasSubmissionPlacement && (
          <FormField
            label="Who was this with?"
            helperText="Asked because this is filed against a buyer submission. A conversation with the buyer advances their contact record."
          >
            <Select
              value={counterpartyRole}
              onChange={(e) =>
                setCounterpartyRole(e.target.value as CommunicationCounterparty | "")
              }
              data-testid="lc-counterparty"
            >
              <option value="">Not specified</option>
              {COUNTERPARTIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        {preFilledOwner && (
          <div className="rounded-md bg-white-warm border border-border-light px-space-3 py-space-2 text-small text-onyx">
            Linked to{" "}
            <strong>
              {preFilledOwner.label ??
                `${ownerTypeLabel(preFilledOwner.type)} · ${preFilledOwner.id}`}
            </strong>
            . You can add additional placements but cannot remove this one.
          </div>
        )}
      </div>
    </Modal>
  );
}

interface ParticipantsEditorProps {
  rows: ParticipantRow[];
  error?: string;
  onChange: (rows: ParticipantRow[]) => void;
  onPatch: (key: string, patch: Partial<CreateParticipantDto>) => void;
}

function ParticipantsEditor({
  rows,
  error,
  onChange,
  onPatch,
}: ParticipantsEditorProps) {
  const addRow = () =>
    onChange([...rows, { _key: newKey(), displayName: "" }]);
  const removeRow = (key: string) =>
    onChange(rows.filter((r) => r._key !== key));

  return (
    <FormField
      label="Participants"
      required
      error={error}
      helperText="Search by name or add a free-text contact."
    >
      <div className="flex flex-col gap-space-2">
        {rows.map((row, idx) => (
          <div
            key={row._key}
            className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-space-2 items-start"
          >
            <ContactPickerInput
              displayName={row.displayName}
              phoneOrEmail={row.phoneOrEmail ?? ""}
              onPick={(picked) =>
                onPatch(row._key, {
                  displayName: picked.displayName,
                  phoneOrEmail: picked.phoneOrEmail,
                  contactId: picked.contactId,
                })
              }
              onChangeFreeText={(name) =>
                onPatch(row._key, { displayName: name, contactId: null })
              }
              ariaLabel={`Participant ${idx + 1} name`}
            />
            <Input
              value={row.phoneOrEmail ?? ""}
              onChange={(e) =>
                onPatch(row._key, { phoneOrEmail: e.target.value })
              }
              placeholder="Phone / email"
              aria-label={`Participant ${idx + 1} phone or email`}
            />
            <Input
              value={row.roleHint ?? ""}
              onChange={(e) => onPatch(row._key, { roleHint: e.target.value })}
              placeholder="Role hint"
              aria-label={`Participant ${idx + 1} role`}
            />
            <button
              type="button"
              onClick={() => removeRow(row._key)}
              disabled={rows.length === 1}
              className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-mid hover:text-status-error disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Remove participant ${idx + 1}`}
            >
              <Trash2 size={14} aria-hidden />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="self-start inline-flex items-center gap-space-1 text-label text-gold-shadow hover:text-gold font-display font-semibold uppercase tracking-label"
        >
          <Plus size={14} aria-hidden /> Add participant
        </button>
      </div>
    </FormField>
  );
}

interface PlacementsEditorProps {
  rows: PlacementRow[];
  error?: string;
  onChange: (rows: PlacementRow[]) => void;
}

function PlacementsEditor({ rows, error, onChange }: PlacementsEditorProps) {
  const ownerTypes = useMemo(() => COMMUNICATION_OWNER_TYPES, []);
  const setEligible = (key: string, packageEligible: boolean) =>
    onChange(
      rows.map((r) => (r._key === key ? { ...r, packageEligible } : r)),
    );
  const addRow = () =>
    onChange([
      ...rows,
      { _key: newKey(), ownerType: "broker", ownerId: "" },
    ]);

  return (
    <FormField
      label="Placements"
      required
      error={error}
      helperText="Where this communication should appear. Pick one or more entities."
    >
      <div className="flex flex-col gap-space-2">
        {rows.map((row, idx) => (
          <div key={row._key} className="flex flex-col gap-space-2">
          <div
            className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-space-2 items-start"
          >
            <Select
              value={row.ownerType}
              onChange={(e) =>
                onChange(
                  rows.map((r) =>
                    r._key === row._key
                      ? { ...r, ownerType: e.target.value, ownerId: "", label: undefined }
                      : r,
                  ),
                )
              }
              disabled={row.locked}
              aria-label={`Placement ${idx + 1} type`}
            >
              {ownerTypes.map((t) => (
                <option key={t} value={t}>
                  {ownerTypeLabel(t)}
                </option>
              ))}
            </Select>
            <PlacementOwnerPicker
              ownerType={row.ownerType}
              ownerId={row.ownerId}
              ownerLabel={row.label}
              disabled={row.locked}
              ariaLabel={`Placement ${idx + 1} id`}
              onChange={(ownerId, label) =>
                onChange(
                  rows.map((r) =>
                    r._key === row._key
                      ? { ...r, ownerId, label: label ?? r.label }
                      : r,
                  ),
                )
              }
            />
            <button
              type="button"
              onClick={() =>
                onChange(rows.filter((r) => r._key !== row._key))
              }
              disabled={row.locked}
              title={
                row.locked
                  ? "This placement is locked because the entry was logged from this entity."
                  : undefined
              }
              className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-mid hover:text-status-error disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Remove placement ${idx + 1}`}
            >
              <Trash2 size={14} aria-hidden />
            </button>
          </div>
          {/* 🔴 Per-PLACEMENT, and that is the point: one entry may carry two
              submission placements, and a note that belongs in listing B's
              package must not leak into listing A's. Not rendered for any other
              owner type — no other placement produces a package. */}
          {row.ownerType === SUBMISSION_OWNER_TYPE && row.ownerId.trim() && (
            <fieldset
              className="m-0 border-0 p-0 pl-space-3 flex flex-col gap-space-1"
              data-testid={`lc-eligibility-${idx}`}
            >
              <legend className="text-label text-gray-mid uppercase tracking-label mb-space-1">
                Who can see this
                {row.label ? ` — ${row.label}` : ""}
              </legend>

              <label className="flex items-start gap-space-2 text-small text-onyx">
                <input
                  type="radio"
                  name={`lc-eligibility-${row._key}`}
                  className="mt-[3px]"
                  checked={!row.packageEligible}
                  onChange={() => setEligible(row._key, false)}
                />
                <span>
                  <span className="block">Internal only</span>
                  <span className="block text-caption text-gray-mid">
                    Phoenix staff only. Never leaves the building.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-space-2 text-small text-onyx">
                <input
                  type="radio"
                  name={`lc-eligibility-${row._key}`}
                  className="mt-[3px]"
                  checked={!!row.packageEligible}
                  onChange={() => setEligible(row._key, true)}
                />
                <span>
                  <span className="block">May appear in a package</span>
                  <span className="block text-caption text-gray-mid">
                    Can be used to write the package sent to this listing's
                    broker. Usually reworded rather than quoted.
                  </span>
                </span>
              </label>

              {row.packageEligible && (
                <p className="text-caption text-status-warning m-0">
                  This note can be read by someone outside Phoenix. Write it for
                  that audience.
                </p>
              )}
            </fieldset>
          )}
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="self-start inline-flex items-center gap-space-1 text-label text-gold-shadow hover:text-gold font-display font-semibold uppercase tracking-label"
        >
          <Plus size={14} aria-hidden /> Add placement
        </button>
      </div>
    </FormField>
  );
}
